<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { format } from 'date-fns';
import Button from 'dashboard/components-next/button/Button.vue';
import CardTypeIcon from './CardTypeIcon.vue';
import CardStageBadge from './CardStageBadge.vue';
import CardPriority from './CardPriority.vue';
import CardDueDate from './CardDueDate.vue';
import CardAssignees from './CardAssignees.vue';
import CardStatusBadge from './CardStatusBadge.vue';
import CardPropertyCode from './CardPropertyCode.vue';

const props = defineProps({
  cards: { type: Array, required: true },
  // Every stage of the board, in order: a card names its stage by id, and the position colors it.
  stages: { type: Array, required: true },
  statusLabels: { type: Object, default: () => ({}) },
  hasMore: { type: Boolean, default: false },
  isLoading: { type: Boolean, default: false },
});

const emit = defineEmits(['open', 'loadMore']);

const { t } = useI18n();

const COLUMNS = computed(() => [
  t('CARDS.TABLE.TITLE'),
  t('CARDS.TABLE.STAGE'),
  t('CARDS.TABLE.PRIORITY'),
  t('CARDS.TABLE.CONTACT'),
  t('CARDS.TABLE.ASSIGNEE'),
  t('CARDS.TABLE.DUE'),
  t('CARDS.TABLE.UPDATED'),
]);

const stagesById = computed(() =>
  Object.fromEntries(
    props.stages.map((stage, index) => [stage.id, { stage, index }])
  )
);
</script>

<template>
  <div class="h-full px-4 pb-4 overflow-auto sm:px-6">
    <p
      v-if="!cards.length && !isLoading"
      class="py-16 mb-0 text-sm text-center text-n-slate-11"
      data-testid="cards-table-empty"
    >
      {{ $t('CARDS.INDEX.EMPTY') }}
    </p>
    <table
      v-else
      class="w-full text-sm border-separate border-spacing-0 min-w-[64rem]"
    >
      <thead class="sticky top-0 z-10 bg-n-background">
        <tr>
          <th
            class="w-16 px-3 py-2 text-xs font-medium border-b ltr:text-left rtl:text-right text-n-slate-11 border-n-weak"
          >
            #
          </th>
          <th
            v-for="column in COLUMNS"
            :key="column"
            class="px-3 py-2 text-xs font-medium border-b ltr:text-left rtl:text-right text-n-slate-11 border-n-weak whitespace-nowrap"
          >
            {{ column }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="card in cards"
          :key="card.id"
          class="transition-colors cursor-pointer hover:bg-n-alpha-1"
          :data-card-id="card.id"
          @click="emit('open', card)"
        >
          <td class="px-3 py-2.5 border-b border-n-weak text-n-slate-10">
            #{{ card.id }}
          </td>
          <td class="px-3 py-2.5 border-b border-n-weak max-w-96">
            <div class="flex items-center min-w-0 gap-2">
              <CardTypeIcon
                :icon="card.task_summary.first?.type_icon"
                :title="card.task_summary.first?.type_name"
              />
              <button
                type="button"
                class="p-0 m-0 text-sm font-medium leading-5 text-start truncate bg-transparent border-0 rounded-sm text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand"
                :class="{
                  'line-through !text-n-slate-11': card.status === 'resolved',
                }"
                data-testid="card-open"
                @click.stop="emit('open', card)"
              >
                {{ card.name }}
              </button>
              <CardPropertyCode
                v-if="card.property_code"
                :code="card.property_code"
              />
              <CardStatusBadge
                v-if="card.status !== 'open'"
                :status="card.status"
                :labels="statusLabels"
              />
            </div>
          </td>
          <td class="px-3 py-2.5 border-b border-n-weak">
            <CardStageBadge
              v-if="stagesById[card.stage_id]"
              :stage="stagesById[card.stage_id].stage"
              :index="stagesById[card.stage_id].index"
            />
          </td>
          <td class="px-3 py-2.5 border-b border-n-weak">
            <CardPriority :priority="card.priority" show-label />
          </td>
          <td
            class="px-3 py-2.5 border-b border-n-weak text-n-slate-12 whitespace-nowrap"
          >
            {{ card.contacts[0]?.name }}
          </td>
          <td class="px-3 py-2.5 border-b border-n-weak">
            <div class="flex items-center gap-2">
              <CardAssignees :assignees="card.assignees" />
              <span
                v-if="card.assignees.length === 1"
                class="truncate text-n-slate-12 whitespace-nowrap max-w-40"
              >
                {{ card.assignees[0].name }}
              </span>
            </div>
          </td>
          <td class="px-3 py-2.5 border-b border-n-weak">
            <CardDueDate :card="card" />
          </td>
          <td
            class="px-3 py-2.5 border-b border-n-weak text-n-slate-11 whitespace-nowrap"
          >
            {{ format(new Date(card.updated_at), 'dd/MM HH:mm') }}
          </td>
        </tr>
      </tbody>
    </table>
    <div v-if="hasMore" class="flex justify-center pt-4">
      <Button
        :label="$t('CARDS.INDEX.LOAD_MORE')"
        size="sm"
        variant="faded"
        color="slate"
        :is-loading="isLoading"
        data-testid="cards-table-load-more"
        @click="emit('loadMore')"
      />
    </div>
  </div>
</template>
